import React from "react";
import { Card, Col, Empty, Row, Segmented, Skeleton, Space, Table, Typography } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import trans from "../../../../utils";
import DeltaBadge from "../widgets/DeltaBadge";
import { NEUTRAL_BAND_PP } from "./retailCompare";

const { Text, Title } = Typography;

const SELECTED_COLOR = "#0f6936";
const COMPARISON_COLOR = "#0284c7";

// Legend swatch: selected month is a solid line, comparison month dashed, so
// the two never rely on colour alone (matches the trend chart).
function MonthSwatch({ color, dashed, label, caption }) {
  return (
    <Space size={6} align="center">
      <svg width="22" height="8" aria-hidden="true">
        <line
          x1="1"
          y1="4"
          x2="21"
          y2="4"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={dashed ? "5 4" : undefined}
        />
      </svg>
      <Text strong style={{ color: "#1e293b" }}>
        {label}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {caption}
      </Text>
    </Space>
  );
}

function MoverList({ changes, emptyText, labels, positiveIsGood }) {
  return (
    <div style={{ minWidth: 0 }}>
      {changes.length ? (
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: 6,
            maxHeight: changes.length > 6 ? 420 : undefined,
            overflowY: changes.length > 6 ? "auto" : undefined,
            paddingInlineEnd: changes.length > 6 ? 4 : undefined,
          }}
        >
          {changes.map((change) => (
            <li
              key={change.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 8,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <span style={{ minWidth: 0 }}>
                <Text ellipsis={{ tooltip: change.label }} style={{ display: "block", maxWidth: 320, color: "#1e293b" }}>
                  {change.label}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <bdi>
                    {change.beforeText} → {change.afterText}
                  </bdi>
                  {change.checksText ? ` · ${change.checksText}` : ""}
                </Text>
              </span>
              <DeltaBadge
                delta={change.delta}
                positiveIsGood={positiveIsGood}
                neutralBand={NEUTRAL_BAND_PP}
                lowSample={change.lowSample}
                from={change.beforeText}
                to={change.afterText}
                fromLabel={labels.from}
                toLabel={labels.to}
              />
            </li>
          ))}
        </ol>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {emptyText}
        </Text>
      )}
    </div>
  );
}

const comparisonColumns = (firstTitle, comparisonLabel, selectedLabel) => [
  {
    title: firstTitle,
    dataIndex: "label",
    key: "label",
    render: (value, row) => (
      <span>
        <Text strong style={{ color: "#1e293b" }}>
          {value}
        </Text>
        {row.hint ? (
          <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
            {row.hint}
          </Text>
        ) : null}
      </span>
    ),
  },
  {
    title: comparisonLabel,
    dataIndex: "from",
    key: "from",
    align: "right",
    render: (value) => <Text style={{ color: "#475569" }}>{value}</Text>,
  },
  {
    title: selectedLabel,
    dataIndex: "to",
    key: "to",
    align: "right",
    render: (value) => (
      <Text strong style={{ color: "#1e293b" }}>
        {value}
      </Text>
    ),
  },
  {
    title: trans("Change"),
    key: "change",
    align: "right",
    render: (_, row) => (
      <DeltaBadge
        variant="outcome"
        unit={row.unit}
        delta={row.delta}
        status={row.status}
        positiveIsGood={row.positiveIsGood}
        neutralBand={row.unit === "points" ? NEUTRAL_BAND_PP : 0}
        lowSample={row.lowSample}
        from={row.from}
        to={row.to}
        fromLabel={comparisonLabel}
        toLabel={selectedLabel}
        note={row.label}
      />
    ),
  },
];

/**
 * Month-vs-month summary: the two months side by side for every headline
 * metric, plus the biggest out-of-stock improvements and declines.
 */
export default function RetailComparisonSummary({
  status,
  selectedLabel,
  comparisonLabel,
  periodText,
  metrics,
  moverDimension,
  moverDimensionOptions,
  onMoverDimensionChange,
  movers,
}) {
  const labels = { from: comparisonLabel, to: selectedLabel };
  const [moverTab, setMoverTab] = React.useState("improved");
  const moverTabs = [
    { key: "improved", label: trans("Improved"), empty: trans("No notable improvements") },
    { key: "declined", label: trans("Declined"), empty: trans("No notable declines") },
    { key: "stable", label: trans("Stable"), empty: trans("No stable rows") },
  ];
  const activeMoverTab =
    moverTabs.find((tab) => tab.key === moverTab) || moverTabs[0];

  return (
    <Card
      role="region"
      aria-label={`${trans("Month comparison")}: ${selectedLabel} ${trans("vs")} ${comparisonLabel}`}
      style={{
        marginBottom: 16,
        borderRadius: 14,
        border: "1px solid #e2e8f0",
        boxShadow: "0 6px 24px rgba(15, 23, 42, 0.05)",
      }}
      bodyStyle={{ padding: "18px 20px" }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <Space size={10} align="start">
          <span
            style={{
              width: 34,
              height: 34,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 9,
              color: SELECTED_COLOR,
              background: "#e8f5ed",
            }}
          >
            <SwapOutlined />
          </span>
          <span>
            <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
              <bdi>{selectedLabel}</bdi> {trans("vs")} <bdi>{comparisonLabel}</bdi>
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {periodText}
            </Text>
          </span>
        </Space>
        <Space size={16} wrap>
          <MonthSwatch color={SELECTED_COLOR} label={selectedLabel} caption={trans("Selected month")} />
          <MonthSwatch color={COMPARISON_COLOR} dashed label={comparisonLabel} caption={trans("Comparison month")} />
        </Space>
      </div>

      {status === "loading" ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <Row gutter={[20, 16]}>
          <Col xs={24} xl={13}>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={metrics}
              scroll={{ x: 520 }}
              columns={comparisonColumns(trans("Metric"), comparisonLabel, selectedLabel)}
            />
          </Col>
          <Col xs={24} xl={11}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <Text strong style={{ color: "#1e293b" }}>
                {trans(
                  movers?.complete
                    ? "Shelf-availability changes"
                    : "Biggest shelf-availability changes",
                )}
              </Text>
              <Segmented
                size="small"
                value={moverDimension}
                onChange={onMoverDimensionChange}
                options={moverDimensionOptions}
              />
            </div>
            {movers ? (
              <>
                <Segmented
                  block
                  value={activeMoverTab.key}
                  onChange={setMoverTab}
                  options={moverTabs.map((tab) => ({
                    value: tab.key,
                    label: `${tab.label} (${movers[tab.key]?.length || 0})`,
                  }))}
                  style={{ marginBottom: 10 }}
                />
                <MoverList
                  changes={movers[activeMoverTab.key] || []}
                  emptyText={activeMoverTab.empty}
                  labels={labels}
                  positiveIsGood
                />
                {movers.unmatched?.length ? (
                  <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                    {trans("Not comparable (no stock checks in one of the months)")}:{" "}
                    {movers.unmatched
                      .map((change) => `${change.label} (${change.beforeText} → ${change.afterText})`)
                      .join(" · ")}
                  </Text>
                ) : null}
              </>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={trans("No data")} />
            )}
            <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 11 }}>
              {trans(
                movers?.complete
                  ? "Showing every row for the selected filter. Higher shelf availability is better. Stable means a change of less than 0.5 points."
                  : "Only rows with at least 10 stock checks in both months are ranked. Higher shelf availability is better. Stable means a change of less than 0.5 points.",
              )}
            </Text>
          </Col>
        </Row>
      )}
    </Card>
  );
}
